import type { Map as MLMap } from 'maplibre-gl';

import { isMapAlive } from './isMapAlive';

/**
 * installMapResizeGuard — re-attach the MapLibre resize protection that legacy
 * `MapCanvas.tsx` always had but the V2 rewrite (`pages/map/v2.tsx`) dropped.
 *
 * THE BUG IT FIXES (V2 /map white canvas on first load)
 * -----------------------------------------------------
 * The V2 page creates the MapLibre instance gated on `hydrated`, which resolves
 * asynchronously. The 2FA banner (`StackedBanners` in `AppShell`) mounts only
 * after `/auth/me` resolves — frequently AFTER the map is created. The banner
 * shrinks the `<main>` content area (documented adaptive-layout grabля, AK-33),
 * so the map container's height changes underneath an already-initialised GL
 * canvas. With no `map.resize()` the WebGL drawing buffer keeps its stale size
 * and the render loop parks without compositing a frame → blank white canvas
 * (observed `isStyleLoaded()=false`, `isMoving()` stuck `true`,
 * `areTilesLoaded()=true`). A manual `map.resize()` instantly repaints — which
 * is exactly what this guard automates.
 *
 * It does four things, mirroring the proven legacy block
 * (`MapCanvas.tsx:495-513`):
 *   1. ResizeObserver on the container → `map.resize()` on every size change
 *      (covers the 2FA banner appearing/disappearing, sidebar, viewport).
 *   2. A forced `resize()` after the map `load` event (inside rAF so layout has
 *      settled) → guarantees the canvas matches the container at first paint
 *      even if no observable size change fires.
 *   3. If the map already fired `load` before the guard is installed (race),
 *      resize immediately.
 *   4. A repaint kick when the document returns to `visible` (see below).
 *
 * THE SECOND BUG IT FIXES (map created in a hidden/occluded tab)
 * -------------------------------------------------------------
 * MapLibre drives everything from its `requestAnimationFrame` render loop, and
 * a *tile request is issued from `_render`* — not from map construction. A
 * background / occluded / automation Chrome tab throttles (or freezes) rAF, so
 * a map created there paints ZERO frames and therefore fetches ZERO tiles,
 * while its DOM (canvas element, attribution, our overlay controls) renders
 * normally. The result looks exactly like a dead component: white map block,
 * an empty network log, no console errors. Reproduced 2026-07-27 by parking
 * rAF in the geometry-tab harness: 0 tile requests parked → 36 the moment the
 * loop resumed. Same class as the staging «NDVI не работает» false alarm
 * (2026-06-26), where a manual `resize()` was what repainted the canvas.
 *
 * Chrome normally replays the deferred frame when the tab is foregrounded, but
 * a page that got *frozen* (bfcache / lifecycle freeze) can come back without
 * one — so we explicitly resize + `triggerRepaint()` on `visibilitychange` to
 * `visible` and on `pageshow`. Both are cheap no-ops for a healthy map.
 *
 * THE THIRD BUG IT FIXES (AK-652 — resize() itself blanked the canvas)
 * ------------------------------------------------------------------
 * The guard above shipped as a bare `map.resize()`, and that turned out to be
 * the *cause* of the 18.08 white-canvas relapse rather than its cure. Two
 * verified facts from the pinned maplibre-gl 5.24 source:
 *
 *   1. `Map._resizeCanvas()` assigns `canvas.width` / `canvas.height`
 *      UNCONDITIONALLY — even when the container size did not change. Writing
 *      those attributes reallocates the WebGL drawing buffer, and a fresh
 *      buffer is EMPTY. So every `resize()` blanks the map until the next
 *      composited frame.
 *   2. `Map.resize()` does NOT schedule that frame directly. It repaints only
 *      as a side effect of `fire('move') → _update(false) → triggerRepaint()`,
 *      and the move events are fired only `if (fireMoving = !this._moving)`.
 *      `_update()` additionally early-returns while `style._loaded === false`.
 *      → a `resize()` landing mid-camera-flight (the initial
 *      `fitBoundsToScope` ease is 600 ms and runs exactly while `/auth/me`
 *      resolves and mounts the 2FA banner) or before style load clears the
 *      buffer and schedules NOTHING. The render loop stays parked with an
 *      empty buffer: `loaded()` / `isStyleLoaded()` true, tiles fetched 200,
 *      canvas alive and correctly sized — and the map white. A manual
 *      `map.resize()` from the console then repaints instantly (by then the
 *      camera is idle, so the move events do fire) — the exact founder/RM
 *      signature of AK-652.
 *
 * MapLibre's OWN container observer (`Map._setupResizeObserver`) never leaves
 * that state because it always follows up with `redraw()` — but it also
 * **discards its first delivery** (`initialResizeEventCaptured`) and throttles
 * the rest by 50 ms. On a cold load the banner-driven shrink IS that discarded
 * first delivery, so our observer is the only handler that runs — and it was
 * the one clearing the buffer without a repaint.
 *
 * Fix: every resize path in this guard is now `resize()` + an explicit repaint
 * (mirroring what MapLibre does internally), plus a guaranteed post-mount kick
 * after the first layout has settled (double rAF) so the first paint matches
 * the settled container even when neither `load` nor an observer delivery
 * happens to land after the banner. Nothing here touches source/tile logic
 * (`setTiles` / `clearTiles`) — the MVT-filter contract is untouched.
 *
 * Returns a cleanup that disconnects the observer, cancels pending frames and
 * removes the visibility listeners. Every map call is guarded by `isMapAlive`
 * plus try/catch: these callbacks can fire after `map.remove()` during a fast
 * unmount/remount (teardown grabля AK-343).
 *
 * @param map       the MapLibre map instance
 * @param container the element the map renders into (the `containerRef` div)
 */
export function installMapResizeGuard(map: MLMap, container: HTMLElement): () => void {
  let disposed = false;

  /**
   * Force one composited frame. `resize()` alone does NOT guarantee it.
   *
   * MapLibre's own observer uses `redraw()` (renders synchronously); we use
   * `triggerRepaint()` — it schedules the frame and coalesces (no-op while a
   * frame is already pending), so a continuous drag of the window/sidebar
   * cannot turn into a storm of synchronous renders. One frame of latency is
   * invisible; a blank canvas is not.
   */
  const repaint = () => {
    try {
      map.triggerRepaint();
    } catch {
      // map already destroyed (unmount race) — ignore
    }
  };

  const safeResize = () => {
    // Teardown gate: never touch a removed map (its style is null → throws).
    if (disposed || !isMapAlive(map)) return;
    try {
      map.resize();
    } catch {
      // map already destroyed (unmount race) — ignore
      return;
    }
    // MANDATORY: resize() just emptied the drawing buffer (fact 1 above) and
    // only *maybe* scheduled a frame (fact 2). Make the frame certain.
    repaint();
  };

  // 1. React to container size changes (2FA banner mount, sidebar, viewport).
  const resizeObserver = new ResizeObserver(() => safeResize());
  resizeObserver.observe(container);

  // 2. Guaranteed kick once the FIRST layout has settled — independent of the
  // `load` event and of any observer delivery. Double rAF: the first frame
  // flushes React's commit, the second runs after layout/paint, so the
  // container is measured at its final (post-banner) height.
  let rafOuter = 0;
  let rafInner = 0;
  rafOuter = requestAnimationFrame(() => {
    rafInner = requestAnimationFrame(safeResize);
  });

  // 3 / 4. Force a resize once the style is loaded so the GL canvas matches the
  // settled container size at first paint.
  if (map.loaded()) {
    safeResize();
  } else {
    map.once('load', () => {
      // rAF: let the layout settle (banner height, flex) before measuring.
      requestAnimationFrame(safeResize);
    });
  }

  // 5. Repaint kick when the tab comes back to the foreground. A map created
  // while the document was hidden has a parked render loop → no frame, and
  // therefore no tile requests at all. resize() + repaint restart it.
  const onVisible = () => {
    if (disposed) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }
    // safeResize() already ends in a forced repaint — the parked render loop
    // restarts from there.
    safeResize();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pageshow', onVisible);

  return () => {
    disposed = true;
    if (rafOuter) cancelAnimationFrame(rafOuter);
    if (rafInner) cancelAnimationFrame(rafInner);
    resizeObserver.disconnect();
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pageshow', onVisible);
  };
}
